import cn from 'classnames';
import brokenImage from 'media/images/broken-image.png';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Page } from 'types/api/site';
import { isInIframe, normalizeUrl } from 'utils/proxyUtils';
import { getSelectedSiteId } from '../../../store/siteInfoSlice';
import { setPageId } from '../../../store/tasksSlice';
import { StatusBadge } from './status-badge';

interface PageProps {
  page: Page;
}

export const PageCard = ({ page }: PageProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const siteId = useSelector(getSelectedSiteId);
  const [hasErrored, setHasErrored] = useState(false);

  const isActive = () => {
    try {
      const pageUrl = normalizeUrl(window.ATARIM ? page.url : page.target_url);
      const currentUrl = normalizeUrl(window.location.href);
      return pageUrl === currentUrl;
    } catch (error) {
      console.error('Error comparing URLs:', error);
      return false;
    }
  };

  const handlePageClick = () => {
    try {
      if (isActive()) return;
      const urlToUse = isInIframe() ? page.url : page.target_url;
      const normalizedUrl = normalizeUrl(urlToUse, siteId);
      if (!normalizedUrl) return;
      const pageUrl = new URL(normalizedUrl);
      const currentSearchParams = new URLSearchParams(window.location.search);
      pageUrl.searchParams.append('pageId', page.id);
      pageUrl.searchParams.append('fromPageClick', 'true');
      pageUrl.searchParams.append('branch', currentSearchParams.get('branch') || '');
      dispatch(setPageId(page.id));
      window.location.href = pageUrl.toString();
    } catch (error) {
      console.error('Error handling page click:', error);
    }
  };

  const onImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (!hasErrored) {
      e.currentTarget.src = brokenImage;
      setHasErrored(true);
    }
  };

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-2 rounded-xl border-2 border-gray-400 p-2 dark:border-border',
        isActive() ? 'border-tertiary cursor-not-allowed' : 'cursor-pointer'
      )}
      onClick={handlePageClick}
      aria-label={t('page_card.aria_label', 'Page preview for {{label}}', { label: page.label })}
    >
      <div className="flex flex-row items-center gap-1 justify-between">
        <span className="text-xs truncate text-textcolor">{page.label.toLowerCase()}</span>
        <span className="bg-destructive flex min-h-4 px-1 items-center justify-center rounded-full align-middle text-xs leading-none text-white"
          aria-label={t('page_card.task_count', '{{count}} tasks', { count: page.total_tasks })}>
          {page.total_tasks}
        </span>
      </div>
      <div className="relative">
        {page.status && (
          <>
            <StatusBadge value={page.status} />
            <div className={cn('absolute top-0 right-0 h-full w-full rounded-md border ',
              page.status === "approved" && "bg-primary/10 border-primary",
              page.status === "in-progress" && "bg-ata-orange/10 border-ata-orange",
              page.status === "needs-review" && "bg-ata-gold/10 border-ata-gold",
            )}>
            </div>
          </>
        )}
        <div className={cn('max-h-[88px] w-full overflow-hidden rounded-md')}>
          <img
            className="h-[200px] w-[400px]"
            src={page.screenshot}
            onError={onImageError}
            alt={t('page_card.screenshot_alt', 'Screenshot of {{label}}', { label: page.label })}
          />
        </div>
      </div>
    </div>
  );
};